import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { blank, Guid } from '@web/utils';
import { map, Observable } from 'rxjs';
import { UserResponse } from '../responses/user.response';
import { UserCollectionResponse } from '../responses/user-collection.response';
import { UserUpdateRequest } from '../requests/user-update.request';
import { UserCreateRequest } from '../requests/user-create.request';

@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  protected readonly provider = inject(ApiDependencyProviderService);

  /* prettier-ignore */
  public fetchDetailsById(id: Guid): Observable<UserResponse> {
    const path = `admin/user/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(UserResponse)));
  }

  public fetchCollection(
    request: CollectionRequest,
  ): Observable<CollectionResponse<UserCollectionResponse>> {
    const path = 'admin/user';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            UserCollectionResponse,
          ),
        ),
      );
  }

  /* prettier-ignore */
  public update(id: Guid, request: UserUpdateRequest): Observable<void> {
    const path = `admin/user/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(request, UserUpdateRequest);

    return this.provider.http
      .put(url, body, { context })
      .pipe(blank());
  }

  public create(request: UserCreateRequest): Observable<void> {
    const path = `admin/user`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      UserCreateRequest,
    );

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  /* prettier-ignore */
  public removeById(id: Guid): Observable<void> {
    const path = `admin/user/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });

    return this.provider.http.delete(url).pipe(blank())
  }

  /* prettier-ignore */
  public resendActivationMessage(id: Guid): Observable<void> {
    const path = `admin/user/${HttpParams.Id}/activation/message/send`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.post(url, body).pipe(blank())
  }

  /* prettier-ignore */
  public activate(id: Guid): Observable<void> {
    const path = `admin/user/${HttpParams.Id}/activate`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const body = this.provider.httpHelper.emptyBody();

    return this.provider.http.put(url, body).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.SSO;
  }
}
